import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Clock } from "lucide-react";
import { toast } from "sonner";

/**
 * One-tap "On My Way" buttons for a job (60-min / 30-min ETAs).
 * - `size`: "sm" (default) for cards, "md" for the Job detail action bar.
 * - Hidden entirely if the job has no customer or is canceled.
 * - Server rejects with a human-readable reason if customer has no phone or
 *   has opted out (STOP); we surface those via toast.error.
 */
export default function OnMyWayButtons({ job, size = "sm" }) {
  const [sending, setSending] = useState(null); // 30 | 60 | null

  if (!job) return null;
  if (job.status === "canceled") return null;
  if (!job.customer_id) return null;

  const send = async (window) => {
    setSending(window);
    try {
      const { data } = await api.post(`/jobs/${job.id}/sms/on-my-way?window=${window}`);
      if (data?.mocked) {
        toast.success(`${window}-min ETA queued (test mode)`);
      } else if (data?.status === "failed") {
        toast.error(data.error_message || `${window}-min ETA send failed`);
      } else {
        toast.success(`${window}-min ETA sent to ${data?.to || "customer"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || `${window}-min ETA failed`);
    } finally {
      setSending(null);
    }
  };

  const btnSize = size === "md" ? "default" : "sm";
  return (
    <div
      className="flex items-center gap-2"
      data-testid={`on-my-way-buttons-${job.id}`}
    >
      <Button
        type="button"
        size={btnSize}
        variant="outline"
        disabled={!!sending}
        onClick={(e) => { e.stopPropagation?.(); send(60); }}
        className="whitespace-nowrap"
        data-testid={`on-my-way-60-${job.id}`}
      >
        <Clock className="w-3.5 h-3.5 mr-1" />
        {sending === 60 ? "Sending…" : "60 min out"}
      </Button>
      <Button
        type="button"
        size={btnSize}
        variant="outline"
        disabled={!!sending}
        onClick={(e) => { e.stopPropagation?.(); send(30); }}
        className="whitespace-nowrap"
        data-testid={`on-my-way-30-${job.id}`}
      >
        <Clock className="w-3.5 h-3.5 mr-1" />
        {sending === 30 ? "Sending…" : "30 min out"}
      </Button>
    </div>
  );
}
